import { instantDateKeyOf } from "@couples/shared";
import { useQuery } from "@tanstack/react-query";
import { BookHeart } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import BottomNav from "../components/BottomNav";
import { NAV_BY_KEY, type NavKey } from "../components/nav";
import { useAuth } from "../lib/auth";
import { fetchLatestOwnEntryAt } from "../lib/diary/entries";
import { fetchMediaByIds } from "../lib/media/notifications";
import { useProfileNav } from "../lib/profileNav";
import { queryKeys } from "../lib/queryKeys";
import { useScheduleNav } from "../lib/scheduleNav";
import { useToast } from "../lib/toast";
import Album from "./Album";
import Diet from "./Diet";
import MediaDetail from "./MediaDetail";
import Diary from "./Diary";
import Places from "./Places";
import Profile from "./Profile";
import Placeholder from "./Placeholder";
import Schedule from "./Schedule";
import Settings from "./Settings";

/** 中身がまだ無いタブ。何がここに来るのかの一行だけを置く(見出しは NAV_ITEMS 側) */
const NOTES: Record<"chores" | "money", string> = {
  chores: "家事の当番と、やった記録をここに置きます。まだ準備中です。",
  money: "支出の記録と口座の残高をここに置きます。まだ準備中です。",
};

function screenOf(
  tab: NavKey,
  scheduleTargetEventId: string | null,
  consumeScheduleTarget: () => void,
  profileWantsPartner: boolean,
  consumeProfileTarget: () => void,
) {
  if (tab === "album") return <Album />;
  if (tab === "diet") return <Diet />;
  if (tab === "schedule") {
    return (
      <Schedule targetEventId={scheduleTargetEventId} onConsumeTarget={consumeScheduleTarget} />
    );
  }
  if (tab === "diary") return <Diary />;
  if (tab === "lists") return <Places />;
  if (tab === "profile") {
    return <Profile wantsPartner={profileWantsPartner} onConsumeTarget={consumeProfileTarget} />;
  }
  if (tab === "settings") return <Settings />;
  return <Placeholder navKey={tab} note={NOTES[tab]} />;
}

/** リロードしても開いていたタブに戻るための保存先 */
const TAB_STORAGE_KEY = "couples:last-tab";

function savedTab(): NavKey {
  const saved = localStorage.getItem(TAB_STORAGE_KEY);
  return saved !== null && saved in NAV_BY_KEY ? (saved as NavKey) : "album";
}

/** 共有リンク(?media=...)で開かれたときの対象。読んだら URL からは消す */
function sharedMediaParam(): string | null {
  return new URLSearchParams(window.location.search).get("media");
}

export default function Shell() {
  const toast = useToast();
  const { targetEventId, consumeTarget } = useScheduleNav();
  const { wantsPartner, consumeTarget: consumeProfileTarget } = useProfileNav();
  const { session } = useAuth();
  const selfId = session?.user.id;
  // 共有リンク経由なら、保存していたタブではなくアルバムを開いて詳細を重ねる
  const [sharedId, setSharedId] = useState<string | null>(sharedMediaParam);
  const [tab, setTab] = useState<NavKey>(() =>
    sharedMediaParam() !== null ? "album" : savedTab(),
  );

  // 予定追加の通知をどの画面で押しても、予定タブへ切り替える
  // (その日を開く処理自体は Schedule 側が targetEventId を見て行う)
  useEffect(() => {
    if (targetEventId === null) return;
    changeTab("schedule");
  }, [targetEventId]);

  // プロフィール変更の通知をどの画面で押しても、プロフィールタブへ切り替える
  // (相手側の表示に切り替える処理自体は Profile 側が wantsPartner を見て行う)
  useEffect(() => {
    if (!wantsPartner) return;
    changeTab("profile");
  }, [wantsPartner]);

  // 今日ぶんの交換日記をまだ書いていなければ、どのタブにいても気づけるようにする
  const latestOwnDiaryEntry = useQuery({
    queryKey: queryKeys.diaryLatestOwn(selfId ?? ""),
    queryFn: () => fetchLatestOwnEntryAt(selfId ?? ""),
    enabled: selfId !== undefined,
  });
  const needsDiaryReminder = useMemo(() => {
    if (!latestOwnDiaryEntry.isSuccess) return false;
    const latest = latestOwnDiaryEntry.data;
    return (
      latest === null || instantDateKeyOf(latest) !== instantDateKeyOf(new Date().toISOString())
    );
  }, [latestOwnDiaryEntry.isSuccess, latestOwnDiaryEntry.data]);

  // 対象はサインイン中のカップルの分しか引けない(RLS)。他人の id なら空になる
  const shared = useQuery({
    queryKey: ["shared-media", sharedId],
    queryFn: () => fetchMediaByIds(sharedId === null ? [] : [sharedId]),
    enabled: sharedId !== null,
  });

  // 開けたかどうかに関わらず、URL のパラメータは一度きりで消す
  // (残すと再読み込みのたびに開き直してしまう)
  useEffect(() => {
    if (sharedMediaParam() === null) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("media");
    window.history.replaceState(null, "", url);
  }, []);

  const sharedMissing = shared.isSuccess && shared.data.length === 0;
  const sharedFailed = shared.isError;
  useEffect(() => {
    if (sharedId === null) return;
    if (sharedMissing) {
      toast("対象が存在しません。", "warn");
      setSharedId(null);
    } else if (sharedFailed) {
      console.error("共有リンクの対象の取得に失敗", shared.error);
      toast("写真・動画の取得に失敗しました。", "error");
      setSharedId(null);
    }
  }, [sharedId, sharedMissing, sharedFailed, shared.error, toast]);

  // アップロード等は画面を移っても走り続け、進捗もヘッダー直下に出続けるので
  // 引き止めはしない(タブを閉じる・再読み込みだけ busy の beforeunload が確認を出す)
  function changeTab(next: NavKey) {
    if (next === tab) return;
    setTab(next);
    localStorage.setItem(TAB_STORAGE_KEY, next);
  }

  return (
    // PC ではナビが左のサイドバーになるので、その幅だけ中身を右に寄せる
    <div className="min-h-full xl:pl-[var(--sidebar-w)]">
      <main className="relative mx-auto flex min-h-full max-w-[var(--content-w)] flex-col pb-20 xl:pb-8">
        {needsDiaryReminder && tab !== "diary" && (
          <button
            type="button"
            onClick={() => {
              changeTab("diary");
            }}
            className="flex items-center gap-2 border-b border-[var(--color-line)] bg-[color-mix(in_srgb,var(--color-accent)_10%,transparent)] px-4 py-2.5 text-left text-sm text-[var(--color-ink)] hover:bg-[color-mix(in_srgb,var(--color-accent)_16%,transparent)]"
          >
            <BookHeart className="size-4 shrink-0 text-[var(--color-accent)]" aria-hidden />
            <span className="flex-1">今日の交換日記がまだです。書いてみましょう。</span>
          </button>
        )}
        {screenOf(tab, targetEventId, consumeTarget, wantsPartner, consumeProfileTarget)}
      </main>
      <BottomNav active={tab} onChange={changeTab} />

      {sharedId !== null && shared.data !== undefined && shared.data.length > 0 && (
        <MediaDetail
          items={shared.data}
          currentId={sharedId}
          onChange={setSharedId}
          onClose={() => {
            setSharedId(null);
          }}
        />
      )}
    </div>
  );
}
